import React from 'react';
import { useForm } from 'react-hook-form';
import { Pressable, ScrollView, View } from 'react-native';

import { yupResolver } from '@hookform/resolvers/yup';

import { Stack } from '@/components/layout';
import { PlayerProfileDetail } from '@/components/player-profile-detail';
import { Button, SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import { GlobalLoadingOverlay } from '@/components/ui/loading/global-loading-overlay';
import Modal from '@/components/ui/modal/modal';
import { STRINGS } from '@/constants/strings';
import { useGuardianProfileQuery } from '@/features/guardian/queries';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import {
  INITIAL_ADD_PLAYER_FORM,
  addPlayerSchema,
  type AddPlayerForm,
} from '@/features/onboarding/schemas/add-player-schema';
import {
  useOnboardingPlayersStore,
  type PendingPlayer,
} from '@/features/onboarding/stores/onboarding-players-store';
import { countryCodeToName, countryNameToCode } from '@/features/onboarding/utils/country-helpers';
import { useIsWindowed, useScaling } from '@/hooks';
import { useAvatarPicker } from '@/hooks/use-avatar-picker';
import { uploadImageToStorage } from '@/libs/firebase/storage/storage-helpers';
import { useUiStore } from '@/stores';
import { useSessionStore } from '@/stores/session-store';
import { colors } from '@/theme/colors';
import { shouldUploadAvatarUri } from '@/utils/helper';
import { useShallow } from 'zustand/shallow';

import { DeletePlayerButton } from './delete-player-button';
import { guardianPlayerToForm, playerToForm, toDateOnly } from './form-helpers';
import { PlayerFormBody } from './player-form-body';
import { useStyles } from './styles';
import { buildViewPlayerDetail } from './view-player-detail';

const strings = STRINGS.onboarding.addPlayer;

type OnboardingAddPlayerSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the sheet is in edit mode: it prefills from this queued player,
   *  saves with `updatePendingPlayer`, and shows a "Delete player" action. */
  player?: PendingPlayer | null;
  /** When set, the sheet is in read-only view mode: it prefills from this saved
   *  server player, shows a "View player" title, and disables every field with no
   *  save/delete actions. Takes precedence over `player`. */
  viewPlayer?: GuardianPlayer | null;
};

/**
 * Bottom sheet for adding a new player linked to the guardian. The check button
 * validates the form and queues the payload (the avatar is uploaded now); the
 * actual POST /api/v1/guardians/me/players happens when step 2's "Continue" is
 * pressed. The level is fixed to 1 for new players, so it is shown disabled and
 * not sent.
 */
export function OnboardingAddPlayerSheet({
  open,
  onOpenChange,
  player,
  viewPlayer,
}: OnboardingAddPlayerSheetProps) {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();

  // `isTablet` is orientation-derived, so rotating swaps the popup `Modal`
  // (landscape) and the bottom `SheetModal` (portrait) — the desired morph. The
  // catch: remounting a native RN Modal in the same commit as the Android
  // orientation config-change leaves the new window unattached, so the surface
  // goes blank and (open still true) can't be reopened. Keep it logically open but
  // briefly drop the native modal across the flip so the old one fully dismisses,
  // then remount the correct variant once rotation has settled. `open` is left
  // untouched, so it re-presents itself in the right layout without a re-tap.
  const [suppressForRotation, setSuppressForRotation] = React.useState(false);
  const prevIsTabletRef = React.useRef(isTablet);
  React.useEffect(() => {
    if (prevIsTabletRef.current === isTablet) return;
    prevIsTabletRef.current = isTablet;
    if (!open) return;
    // Drop the native modal while Android finishes the rotation config-change.
    // Mounting a fresh RN Modal too early (a fresh tap works because it lands well
    // after rotation) leaves the window unattached. Wait for the rotation to settle,
    // then re-present on a clean frame. Tune `ROTATION_SETTLE_MS` if the device needs
    // longer/shorter.
    const ROTATION_SETTLE_MS = 500;
    setSuppressForRotation(true);
    let rafId: number;
    const timeoutId = setTimeout(() => {
      rafId = requestAnimationFrame(() => setSuppressForRotation(false));
    }, ROTATION_SETTLE_MS);
    return () => {
      clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isTablet, open]);
  const showModal = open && !suppressForRotation;

  const isViewing = Boolean(viewPlayer);
  const isEditing = Boolean(player) && !isViewing;
  const sheetTitle = isViewing ? strings.viewTitle : isEditing ? strings.editTitle : strings.title;

  const uid = useSessionStore(s => s.user?.uid);
  const isWindowed = useIsWindowed();
  const { data: guardian } = useGuardianProfileQuery(uid);
  const addPendingPlayer = useOnboardingPlayersStore(s => s.addPendingPlayer);
  const updatePendingPlayer = useOnboardingPlayersStore(s => s.updatePendingPlayer);
  const removePendingPlayer = useOnboardingPlayersStore(s => s.removePendingPlayer);
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const pickAvatar = useAvatarPicker();

  const { control, handleSubmit, reset, setValue } = useForm<AddPlayerForm>({
    resolver: yupResolver(addPlayerSchema) as never,
    defaultValues: INITIAL_ADD_PLAYER_FORM,
    mode: 'onChange',
  });

  // On open: prefill from the server player being viewed, the queued player being
  // edited, or a clean form when adding.
  React.useEffect(() => {
    if (!open) return;
    if (viewPlayer) reset(guardianPlayerToForm(viewPlayer));
    else reset(player ? playerToForm(player) : INITIAL_ADD_PLAYER_FORM);
  }, [open, player, viewPlayer, reset]);

  const handlePickAvatar = () => {
    pickAvatar(value => {
      setValue('avatar', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    });
  };

  // "Same address with me" copies the guardian's address into the player's fields.
  const handleToggleSameAddress = (checked: boolean) => {
    setValue('sameAddress', checked, { shouldDirty: true });
    // The guardian stores the country name; the form field expects the ISO code.
    setValue('country', checked ? countryNameToCode(guardian?.address?.country) : '');
    setValue('city', checked ? (guardian?.address?.city ?? '') : '');
    setValue('state', checked ? (guardian?.address?.state ?? null) : null);
    setValue('zipCode', checked ? (guardian?.address?.zipcode ?? '') : '');
  };

  const close = () => onOpenChange(false);

  // Drop the queued player and close (edit mode only).
  const handleDelete = () => {
    if (player) removePendingPlayer(player.id);
    close();
  };

  // Validate, upload the avatar, then queue (add) or replace (edit) the player.
  // Nothing is sent to the API here — step 2's "Continue" creates every queued
  // player at once.
  const onSubmit = handleSubmit(async values => {
    const gender = values.gender;
    if (!gender) return;

    try {
      showGlobalLoading();

      const avatarUri = values.avatar?.uri;
      let avatarUrl: string | undefined;
      if (uid && shouldUploadAvatarUri(avatarUri)) {
        const resp = await uploadImageToStorage(
          avatarUri,
          `profile-pictures/player/${uid}-${Date.now()}.jpg`,
        );
        avatarUrl = `${resp.downloadUrl}&v=${Date.now()}`;
      } else if (avatarUri) {
        // Already a remote URL (prefilled when editing) — keep it as-is.
        avatarUrl = avatarUri;
      }

      // Height is entered coach-style (e.g. `48"`); strip the inch mark for the numeric API.
      const heightInches = values.height ? Number(values.height.replace(/"/g, '')) : NaN;
      // Country holds the ISO code on the form; the API stores the country name.
      const countryName = countryCodeToName(values.country);
      const hasAddress = Boolean(countryName || values.city || values.state || values.zipCode);
      const payload = {
        firstname: values.firstName.trim(),
        lastname: values.lastName.trim(),
        // Send the calendar day (YYYY-MM-DD) from local parts — `toISOString()` would
        // shift to UTC and can roll the date back a day for users east of UTC.
        dob: toDateOnly(values.dob!),
        gender,
        height: Number.isFinite(heightInches) ? heightInches : undefined,
        needClubs: values.needClubs,
        dominantHand: values.dominantHand,
        address: hasAddress
          ? {
              country: countryName || undefined,
              city: values.city || undefined,
              state: values.state || undefined,
              zipcode: values.zipCode || undefined,
            }
          : undefined,
        avatarUrl,
      };

      if (player) updatePendingPlayer(player.id, payload, values.sameAddress);
      else addPendingPlayer(payload, values.sameAddress);
      close();
    } catch {
      // Keep the sheet open; the error interceptor already reports the failure.
    } finally {
      hideGlobalLoading();
    }
  });

  // View mode shows a read-only "Player Profile Detail" summary card (shared with the
  // co-guardian mutual-kid sheet); add/edit keep the editable form.
  const formBody =
    isViewing && viewPlayer ? (
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.viewBody}>
        <PlayerProfileDetail {...buildViewPlayerDetail(viewPlayer)} />
      </ScrollView>
    ) : (
      <PlayerFormBody
        control={control}
        setValue={setValue}
        isViewing={isViewing}
        isEditing={isEditing}
        isTablet={isTablet}
        isWindowed={isWindowed}
        onPickAvatar={handlePickAvatar}
        onToggleSameAddress={handleToggleSameAddress}
        onDelete={handleDelete}
      />
    );

  // Tablet: a centered popup with a title-only header and a two-button footer
  // (Cancel / Create-or-Save), matching Figma. Submit/validation logic is shared.
  if (isTablet) {
    return (
      <Modal visible={showModal} contentContainerStyle={styles.popupCenter}>
        <View style={styles.popupCard}>
          <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.popupTitle}>
            {sheetTitle}
          </Text>
          {formBody}
          <View style={styles.popupFooter}>
            {/* View mode is read-only: a single "Close" button, no save/delete. */}
            {isViewing ? (
              <View style={[styles.popupActions, styles.popupActionsCenter]}>
                <Button variant="outline" onPress={close} style={styles.viewCloseButton}>
                  <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
                    {strings.close}
                  </Button.Text>
                </Button>
              </View>
            ) : (
              <View style={styles.popupActions}>
                <Button variant="outline" onPress={close} style={styles.footerButton}>
                  <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
                    {strings.cancel}
                  </Button.Text>
                </Button>
                <Button onPress={onSubmit} style={styles.footerButton}>
                  <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
                    {isEditing ? strings.save : strings.create}
                  </Button.Text>
                </Button>
              </View>
            )}
          </View>
          {/* The app-root overlay renders below this native modal, so render our own. */}
          <GlobalLoadingOverlay />
        </View>
      </Modal>
    );
  }

  // Phone / landscape sheet: header with close (left) + check-icon submit (right).
  return (
    <SheetModal open={showModal} onOpenChange={onOpenChange}>
      <SheetContent style={styles.sheet} closeOnBackdropPress>
        {/* Header: close / title / submit */}
        <Stack
          direction="row"
          align="center"
          justify="space-between"
          paddingX={scale(20)}
          paddingY={scale(16)}>
          <Pressable onPress={close} hitSlop={8}>
            <SvgIcon name="IcXClose" size={scale(24)} color={colors['10']} />
          </Pressable>
          <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
            {sheetTitle}
          </Text>
          {/* View mode is read-only: no submit, just a spacer to keep the title centered. */}
          {isViewing ? (
            <View style={{ width: scale(24) }} />
          ) : (
            <Pressable onPress={onSubmit} hitSlop={8}>
              <SvgIcon name="IcCheckMd" size={scale(24)} color={colors.Blue} />
            </Pressable>
          )}
        </Stack>
        {formBody}
        {isEditing ? <DeletePlayerButton onPress={handleDelete} /> : null}
      </SheetContent>
    </SheetModal>
  );
}

export default OnboardingAddPlayerSheet;
